import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/SupportPage.css";
import { ContactValidators } from "../helpers/validationHelper";
import { postBhpClientLoginIssue } from "../helpers/apiHelper";

const SupportPage = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");
  const [issueType, setIssueType] = useState("");
  const [details, setDetails] = useState("");

  const [touched, setTouched] = useState({
    cpf: false,
    email: false,
    phone: false,
    issueType: false,
    details: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successId, setSuccessId] = useState(null);

  const issueOptions = useMemo(
    () => [
      { value: "", label: "Selecione uma opção" },
      { value: "CPF_INVALID", label: "Meu CPF consta como inválido" },
      {
        value: "EMAIL_CODE_NOT_RECEIVED",
        label: "Não recebi o código por e-mail",
      },
      { value: "SMS_CODE_NOT_RECEIVED", label: "Não recebi o código por SMS" },
      { value: "OTHER", label: "Outro" },
    ],
    [],
  );

  const normalizedEmail = useMemo(() => {
    const v = String(email || "").trim();
    return v ? ContactValidators.normalizeEmail(v) : "";
  }, [email]);

  const normalizedCpf = useMemo(() => ContactValidators.digitsOnly(cpf), [cpf]);

  const normalizedPhoneE164 = useMemo(() => {
    const v = String(phone || "").trim();
    if (!v) return "";
    return ContactValidators.formatPhoneE164BR(v);
  }, [phone]);

  const cpfError = useMemo(() => {
    if (!touched.cpf) return "";
    if (!normalizedCpf) return "Informe o CPF.";
    if (normalizedCpf.length !== 11) return "CPF deve ter 11 dígitos.";
    return ContactValidators.isValidCPF(normalizedCpf) ? "" : "CPF inválido.";
  }, [touched.cpf, normalizedCpf]);

  const emailError = useMemo(() => {
    if (!touched.email) return "";
    if (!normalizedEmail) return "Informe o e-mail.";
    return ContactValidators.isValidEmail(normalizedEmail)
      ? ""
      : "E-mail inválido.";
  }, [touched.email, normalizedEmail]);

  const phoneError = useMemo(() => {
    if (!touched.phone) return "";
    const raw = String(phone || "").trim();
    if (!raw) return "Informe o telefone.";
    return normalizedPhoneE164 ? "" : "Telefone inválido (use DDD + número).";
  }, [touched.phone, phone, normalizedPhoneE164]);

  const issueError = useMemo(() => {
    if (!touched.issueType) return "";
    return issueType ? "" : "Selecione um tipo de problema.";
  }, [touched.issueType, issueType]);

  const detailsError = useMemo(() => {
    if (!touched.details) return "";
    const v = String(details || "").trim();
    if (!v) return "Descreva o problema.";
    if (v.length < 10)
      return "Descreva com mais detalhes (mín. 10 caracteres).";
    return "";
  }, [touched.details, details]);

  const hasBlockingErrors = useMemo(() => {
    return Boolean(
      cpfError || emailError || phoneError || issueError || detailsError,
    );
  }, [cpfError, emailError, phoneError, issueError, detailsError]);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    const allPresent =
      normalizedCpf.length === 11 &&
      normalizedEmail.length > 0 &&
      normalizedPhoneE164.length > 0 &&
      String(issueType || "").trim().length > 0 &&
      String(details || "").trim().length >= 10;
    return allPresent && !hasBlockingErrors;
  }, [
    submitting,
    normalizedCpf,
    normalizedEmail,
    normalizedPhoneE164,
    issueType,
    details,
    hasBlockingErrors,
  ]);

  const markTouched = (key) => setTouched((p) => ({ ...p, [key]: true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSuccessId(null);

    setTouched({
      cpf: true,
      email: true,
      phone: true,
      issueType: true,
      details: true,
    });

    if (!canSubmit) return;

    const payload = {
      caseType: "BHP-Individuals",
      cpf: normalizedCpf,
      phone: normalizedPhoneE164,
      email: normalizedEmail,
      issueType,
      clientDetails: String(details || "").trim(),
    };

    setSubmitting(true);
    try {
      const res = await postBhpClientLoginIssue(payload);
      setSuccessId(res?.id ?? "ok");

      setEmail("");
      setPhone("");
      setCpf("");
      setIssueType("");
      setDetails("");
      setTouched({
        cpf: false,
        email: false,
        phone: false,
        issueType: false,
        details: false,
      });
    } catch (err) {
      setSubmitError(
        err?.message || "Não foi possível enviar. Tente novamente.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="supportPage">
      <div className="supportOverlay">
        <form className="supportModal" onSubmit={handleSubmit}>
          <button
            type="button"
            className="supportClose"
            aria-label="Fechar"
            onClick={() => navigate(-1)}
            disabled={submitting}
          >
            ×
          </button>

          <div className="supportTitle">Precisa de ajuda para entrar?</div>
          <div className="supportSubtitle">
            Confirme seus dados e descreva o problema. Nossa equipe vai analisar
            e ajudar você.
          </div>

          <label className="supportLabel" htmlFor="supportCpf">
            CPF:
          </label>
          <input
            id="supportCpf"
            className="supportInput"
            type="text"
            value={cpf}
            onChange={(e) => setCpf(e.target.value)}
            onBlur={() => markTouched("cpf")}
            placeholder="Somente números"
            autoComplete="cpf"
            inputMode="numeric"
          />
          {cpfError ? <div className="supportError">{cpfError}</div> : null}

          <label className="supportLabel" htmlFor="supportEmail">
            E-mail:
          </label>
          <input
            id="supportEmail"
            className="supportInput"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => markTouched("email")}
            autoComplete="email"
          />
          {emailError ? <div className="supportError">{emailError}</div> : null}

          <label className="supportLabel" htmlFor="supportPhone">
            Telefone:
          </label>
          <input
            id="supportPhone"
            className="supportInput"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={() => markTouched("phone")}
            placeholder="DDD + número"
            autoComplete="tel"
            inputMode="tel"
          />
          {phoneError ? <div className="supportError">{phoneError}</div> : null}

          <label className="supportLabel" htmlFor="supportIssue">
            Tipo de problema:
          </label>
          <select
            id="supportIssue"
            className="supportSelect"
            value={issueType}
            onChange={(e) => setIssueType(e.target.value)}
            onBlur={() => markTouched("issueType")}
          >
            {issueOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {issueError ? <div className="supportError">{issueError}</div> : null}

          <label className="supportLabel" htmlFor="supportDetails">
            Detalhes:
          </label>
          <textarea
            id="supportDetails"
            className="supportTextarea"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            onBlur={() => markTouched("details")}
            placeholder="Conte o máximo de detalhes possível."
            rows={5}
          />
          {detailsError ? (
            <div className="supportError">{detailsError}</div>
          ) : null}

          {submitError ? (
            <div className="supportError">{submitError}</div>
          ) : null}
          {successId ? (
            <div className="supportSuccess">
              Solicitação enviada com sucesso.
            </div>
          ) : null}

          <button className="supportSubmit" type="submit" disabled={!canSubmit}>
            {submitting ? "Enviando..." : "Enviar"}
          </button>

          <button
            type="button"
            className="supportBackLink"
            onClick={() => navigate("/signin")}
            disabled={submitting}
          >
            Voltar para o login
          </button>
        </form>
      </div>
    </div>
  );
};

export default SupportPage;
